/**
 * 区間のタイムラインと、区間ごとの商品の確定 / 取り消し。
 *
 * この面が守っていること:
 * - **商品候補の呼び名から商品を決めない。** 読解が返すのは呼び名（`label`）で、
 *   商品マスターの行とは別物である。確定するときは、人が商品マスターから
 *   選んだものを渡す（呼び名での暗黙の紐付けを作らない）。
 * - **確定は版（`expectedVersion`）つきで送る。** 誰かが先に触っていれば
 *   `CONFLICT` が返る。そのときは黙って上書きせず、読み直す導線を出す。
 * - **確定したときの商品名を、商品マスターから引き直さない。** 確定の中に
 *   控えてある名前をそのまま出す（商品が改名されても、そのとき何を
 *   確定したのかが読めなくなってはいけない）。
 * - 読み取った結果が空の区間を、空の表で埋めない。
 */

import { useState } from "react";
import { Flex, Tag as AntTag } from "antd";
import { useMutation, useQuery } from "urql";
import {
  Button,
  ConfirmSegmentProductDocument,
  Tag,
  UnconfirmSegmentProductDocument,
  VideoSegmentProductChoicesDocument,
  type VideoSegmentFieldsFragment,
} from "visumo-data-agent-web-shared";

import { formatJst } from "../format-time";

/** 候補の出どころの表示名。内部の値そのものは画面に出さない。 */
const BASIS_LABELS: Record<string, string> = {
  ON_SCREEN_TEXT: "画面に出ていた文字から",
  SPOKEN: "話していた言葉から",
  VISUAL: "映っているものの見た目から",
};

/** 根拠の種類の表示名。 */
const EVIDENCE_LABELS: Record<string, string> = {
  FRAME: "静止画",
  TRANSCRIPT: "文字起こし",
};

/** 秒を「1:23」の形にする。**生の秒数を並べない**。 */
export function formatClock(seconds: number): string {
  const whole = Math.max(0, Math.round(seconds));
  const minutes = Math.floor(whole / 60);
  const rest = whole % 60;
  return `${String(minutes)}:${String(rest).padStart(2, "0")}`;
}

/** 確からしさは割合で出す（0.42 のような生値を並べない）。 */
function formatConfidence(value: number): string {
  return `${String(Math.round(value * 100))} %`;
}

function newIdempotencyKey(): string {
  return crypto.randomUUID();
}

/**
 * 確定する商品を、商品マスターから人に選んでもらう。
 * 候補の呼び名は**探すときの初期値**にするだけで、選ぶのは人である。
 */
function ProductPicker({
  initialQuery,
  busy,
  onPick,
  onCancel,
}: {
  initialQuery: string;
  busy: boolean;
  onPick: (productId: string) => void;
  onCancel: () => void;
}) {
  const [text, setText] = useState(initialQuery);
  const [query, setQuery] = useState(initialQuery);
  const [result] = useQuery({
    query: VideoSegmentProductChoicesDocument,
    variables: { query: query === "" ? null : query, first: 10 },
  });

  const products = result.data?.products.edges.map((edge) => edge.node) ?? [];

  return (
    <div className="vd-stack" style={{ marginTop: 8 }}>
      <div className="vd-row">
        <label>
          <span style={{ marginRight: 6 }}>商品を探す</span>
          <input
            aria-label="商品を探す"
            type="search"
            value={text}
            onChange={(event) => {
              setText(event.target.value);
            }}
          />
        </label>
        <Button
          onClick={() => {
            setQuery(text);
          }}
        >
          探す
        </Button>
        <Button onClick={onCancel}>やめる</Button>
      </div>

      {result.error !== undefined ? (
        <p role="alert" aria-label="商品を探せなかった理由">
          商品を探せませんでした: {result.error.message}
        </p>
      ) : result.fetching ? (
        <p className="vd-stat-note">探しています。</p>
      ) : products.length === 0 ? (
        <p aria-label="商品が見つからない">
          この言葉に当てはまる商品は見つかりませんでした。別の言葉で探してください。
        </p>
      ) : (
        <ul className="vd-job-reasons" aria-label="商品の候補">
          {products.map((product) => (
            <li key={product.id}>
              <Button
                disabled={busy}
                onClick={() => {
                  onPick(product.id);
                }}
              >
                {product.name}
                {product.brand === null || product.brand === undefined
                  ? ""
                  : `（${product.brand}）`}
                で確定
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function SegmentCard({
  videoId,
  segment,
  onConflict,
  activeIndex,
  onSeek,
}: {
  videoId: string;
  segment: VideoSegmentFieldsFragment;
  /** 版が食い違ったときに、呼び出し側へ読み直しを促す */
  onConflict: () => void;
  activeIndex?: number | null;
  onSeek?: (index: number, seconds: number) => void;
}) {
  const [picking, setPicking] = useState(false);
  const [errors, setErrors] = useState<readonly string[]>([]);
  const [conflicted, setConflicted] = useState(false);
  const [confirmResult, confirmProduct] = useMutation(
    ConfirmSegmentProductDocument,
  );
  const [unconfirmResult, unconfirmProduct] = useMutation(
    UnconfirmSegmentProductDocument,
  );

  const busy = confirmResult.fetching || unconfirmResult.fetching;
  const confirmation = segment.confirmation ?? null;
  const topCandidate = segment.products[0] ?? null;
  const label = `区間 ${formatClock(segment.startSec)}〜${formatClock(segment.endSec)}`;

  const apply = (
    userErrors: readonly { code: string; message: string }[],
  ): void => {
    setErrors(userErrors.map((error) => error.message));
    const hasConflict = userErrors.some((error) => error.code === "CONFLICT");
    setConflicted(hasConflict);
    if (userErrors.length === 0) {
      setPicking(false);
    }
  };

  const confirm = async (productId: string): Promise<void> => {
    const response = await confirmProduct({
      input: {
        videoId,
        segmentIndex: segment.index,
        productId,
        // まだ確定していなければ 0。確定済みの付け替えは、いまの版を渡す
        expectedVersion: confirmation?.version ?? 0,
        idempotencyKey: newIdempotencyKey(),
      },
    });
    if (response.error || !response.data) {
      setErrors([
        response.error?.message ??
          "商品を確定できませんでした。もう一度お試しください。",
      ]);
      return;
    }
    apply(response.data.confirmSegmentProduct.userErrors);
  };

  const unconfirm = async (): Promise<void> => {
    if (confirmation === null) return;
    const response = await unconfirmProduct({
      input: {
        videoId,
        segmentIndex: segment.index,
        expectedVersion: confirmation.version,
        idempotencyKey: newIdempotencyKey(),
      },
    });
    if (response.error || !response.data) {
      setErrors([
        response.error?.message ??
          "確定を取り消せませんでした。もう一度お試しください。",
      ]);
      return;
    }
    apply(response.data.unconfirmSegmentProduct.userErrors);
  };

  return (
    <section
      aria-label={label}
      onClick={(event) => {
        if (
          event.target instanceof Element &&
          event.target.closest(
            "button, input, a, label, select, [role='button']",
          )
        )
          return;
        onSeek?.(segment.index, segment.startSec);
      }}
      className={`vd-video-segment${activeIndex === segment.index ? " vd-video-segment--active" : ""}`}
    >
      <div className="vd-row" style={{ justifyContent: "space-between" }}>
        <div className="vd-card-title">
          <span>
            #{segment.index + 1} {label}
          </span>
          {onSeek ? (
            <button
              type="button"
              className="vd-video-seek"
              onClick={() => onSeek(segment.index, segment.startSec)}
              aria-label={`${label} を再生`}
            >
              この区間を再生
            </button>
          ) : null}
          {confirmation === null ? null : <Tag tone="ok">確定済み</Tag>}
        </div>
        <div className="vd-stat-note">
          {segment.evidence.length === 0
            ? "根拠は残っていません"
            : segment.evidence
                .map(
                  (item) =>
                    `${EVIDENCE_LABELS[item.kind] ?? item.kind} ${String(item.index + 1)}`,
                )
                .join(" / ")}
        </div>
      </div>

      <p style={{ margin: "4px 0 0" }}>{segment.label}</p>

      <div className="vd-video-meta-row">
        <span className="vd-stat-note">人物</span>
        <div>
          {segment.people == null ? (
            <span className="vd-stat-note">人物は記録されていません</span>
          ) : segment.people.length === 0 ? (
            <span className="vd-stat-note">写っていません</span>
          ) : (
            segment.people.map((person, index) => (
              <div className="vd-video-observation" key={index}>
                <AntTag>{person.roleGuess}</AntTag>
                {person.count} 名 — {person.description}
                <span className="vd-stat-note">
                  {" "}
                  確信度 {formatConfidence(person.confidence)}
                </span>
              </div>
            ))
          )}
        </div>
      </div>
      <div className="vd-video-meta-row">
        <span className="vd-stat-note">商品</span>
        <div>
          {segment.products.length === 0 ? (
            <p className="vd-stat-note">
              この区間では、商品らしいものは見つかりませんでした。
            </p>
          ) : (
            <div aria-label={`${label} の商品候補`}>
              {segment.products.map((candidate, index) => (
                <div
                  className="vd-video-observation"
                  key={`${candidate.label}-${candidate.basis}-${index}`}
                >
                  <div>{candidate.label}</div>
                  {candidate.vocabTags != null &&
                  candidate.vocabTags.length > 0 ? (
                    <Flex gap={4} wrap className="vd-video-vocab">
                      {candidate.vocabTags.map((tag) => (
                        <AntTag key={tag}>{tag}</AntTag>
                      ))}
                    </Flex>
                  ) : null}
                  <AntTag
                    color={
                      candidate.basis === "ON_SCREEN_TEXT"
                        ? "#F5DC73"
                        : candidate.basis === "SPOKEN"
                          ? "#AC7AB3"
                          : "#bfbfbf"
                    }
                  >
                    {BASIS_LABELS[candidate.basis] ?? candidate.basis}
                  </AntTag>
                  <span className="vd-stat-note">
                    確からしさ {formatConfidence(candidate.confidence)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <div className="vd-video-meta-row">
        <span className="vd-stat-note">話題</span>
        <Flex gap={4} wrap>
          {segment.topics == null ? (
            <span className="vd-stat-note">話題は記録されていません</span>
          ) : segment.topics.length === 0 ? (
            <span className="vd-stat-note">特定できていません</span>
          ) : (
            segment.topics.map((topic) => (
              <AntTag color="#EDAA86" key={topic}>
                {topic}
              </AntTag>
            ))
          )}
        </Flex>
      </div>
      <div className="vd-video-meta-row">
        <span className="vd-stat-note">テロップ</span>
        <Flex gap={6} wrap>
          {segment.onScreenText == null ? (
            <span className="vd-stat-note">テロップは記録されていません</span>
          ) : segment.onScreenText.length === 0 ? (
            <span className="vd-stat-note">なし</span>
          ) : (
            segment.onScreenText.map((text) => (
              <span className="vd-video-caption" key={text}>
                {text}
              </span>
            ))
          )}
        </Flex>
      </div>

      {confirmation === null ? null : (
        <p className="vd-stat-note" style={{ margin: "8px 0 0" }}>
          {confirmation.productName} を確定しました（
          {formatJst(confirmation.confirmedAt) ?? "時刻は残っていません"}）。
        </p>
      )}

      {errors.length === 0 ? null : (
        <div className="vd-state vd-state--error" role="alert">
          {errors.map((message) => (
            <span key={message}>{message}</span>
          ))}
          {conflicted ? (
            <Button
              onClick={() => {
                setErrors([]);
                setConflicted(false);
                onConflict();
              }}
            >
              読み直す
            </Button>
          ) : null}
        </div>
      )}

      {picking ? (
        <ProductPicker
          busy={busy}
          initialQuery={topCandidate?.label ?? ""}
          onCancel={() => {
            setPicking(false);
            setErrors([]);
          }}
          onPick={(productId) => {
            void confirm(productId);
          }}
        />
      ) : (
        <div className="vd-row" style={{ marginTop: 8 }}>
          <Button
            disabled={busy}
            onClick={() => {
              setErrors([]);
              setPicking(true);
            }}
          >
            {confirmation === null ? "商品を確定する" : "別の商品に付け替える"}
          </Button>
          {confirmation === null ? null : (
            <Button
              disabled={busy}
              onClick={() => {
                void unconfirm();
              }}
            >
              確定を取り消す
            </Button>
          )}
        </div>
      )}
    </section>
  );
}

export function VideoSegmentList({
  videoId,
  segments,
  onConflict,
  activeIndex,
  onSeek,
}: {
  videoId: string;
  segments: readonly VideoSegmentFieldsFragment[];
  onConflict: () => void;
  activeIndex?: number | null;
  onSeek?: (index: number, seconds: number) => void;
}) {
  return (
    <div className="vd-stack">
      {segments.map((segment) => (
        <SegmentCard
          key={segment.index}
          onConflict={onConflict}
          activeIndex={activeIndex}
          onSeek={onSeek}
          segment={segment}
          videoId={videoId}
        />
      ))}
    </div>
  );
}
